"use client"

import ImageWithTitle from "@/(admin)/modules/ui-builder/config/themes/caocao/CaoCaoCustomizeTour/ImageWithTitle"
import { getImageAlt, getImageSrc } from "@/lib/utils"
import { ComponentConfig } from "@measured/puck"
import Link from "next/link"
import { twMerge } from "tailwind-merge"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface CustomizeTourProps extends CommonStylesProps {
  title?: string
  description?: string
  buttonText?: string
  buttonLink?: string
  tourTypes?: {
    title: string
    imageUrl: string | { url: string; alternativeText?: string }
    link?: string
  }[]
}

export const RenderConfig: ComponentConfig<CustomizeTourProps> = {
  render: ({ className, styles, responsiveType, title, description, buttonText, buttonLink, tourTypes = [] }) => {
    const id = `customize-tour-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    const lang = typeof window !== "undefined" ? document.documentElement.lang || "en" : "en"

    return (
      <>
        {responsiveCSS}
        <div className={`${twMerge(className)} ${id} px-4 lg:px-0 py-10 lg:py-0`}>
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-[15.5625rem] lg:pb-16 pb-10">
            <div className="flex-1 lg:max-w-[534px] max-w-[537px]">
              <h1 className="text-[2.5rem] lg:text-4xl lg:text-[3.375rem] leading-tight lg:leading-[4.5rem] font-bold text-[#0C9443] mb-2 lg:mb-4 text-center lg:text-left">
                {title}
              </h1>
              <p className="text-[#4F4F4F] text-base leading-[1.6875rem] font-normal w-full text-center lg:text-left">
                {description}
              </p>
            </div>
            <div className="w-full lg:max-w-[417px]">
              <Link
                href={buttonLink || ""}
                className="inline-block bg-[#0C9443] text-white px-8 py-3 rounded-[6.25rem] font-semibold w-full text-center hover:bg-[#0a7a3a] hover:text-white transition-colors duration-300"
              >
                {buttonText}
              </Link>
            </div>
          </div>

          {/* Tour Types Layout - Figma Style */}
          {tourTypes.length > 0 && (
            <div className="space-y-6">
              {/* Large Corporate Image - Top */}
              {tourTypes[0] && (
                <Link
                  href={`/${lang === "en" ? "" : "vi/"}design-your-itinerary${
                    lang === "en" ? "" : "-vi"
                  }#${tourTypes[0].title.toLowerCase()}`}
                  className="relative group cursor-pointer block"
                >
                  <ImageWithTitle
                    variant="banner"
                    src={getImageSrc(tourTypes[0].imageUrl)}
                    alt={getImageAlt(tourTypes[0].imageUrl, tourTypes[0].title)}
                    title={tourTypes[0].title}
                  />
                </Link>
              )}

              {/* 2x2 Grid for remaining tour types */}
              {tourTypes.length > 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-6">
                  {tourTypes.slice(1, 5).map((tour, index) => (
                    <Link
                      key={index}
                      href={`/${lang === "en" ? "" : "vi/"}design-your-itinerary${
                        lang === "en" ? "" : "-vi"
                      }#${tour.title.toLowerCase()}`}
                      className="relative group cursor-pointer block rounded-lg overflow-hidden"
                    >
                      <ImageWithTitle
                        variant="square"
                        src={getImageSrc(tour.imageUrl)}
                        alt={getImageAlt(tour.imageUrl, tour.title)}
                        title={tour.title}
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </>
    )
  },
}
